import { useMemo, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { EcoaPage, EcoaPageVisibilitySpec } from "maven-lib/dist/dto/ecoaCrf";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { PreScreeningMultipleQuestionnaire } from "../../../components/home/prescreening/PreScreeningMultipleQuestionnaire";
import { PreScreeningQuestionnaireFooter } from "../../../components/home/prescreening/PreScreeningQuestionnaireFooter";
import { PreScreeningSingleQuestionnaire } from "../../../components/home/prescreening/PreScreeningSingleQuestionnaire";
import { isConditionalMatched } from "../../../lib";
import { PreScreeningCaptureItemSpec } from "../preprocess/PreScreening";
import { useIntParam, useRoute } from "@maven-surface/hooks/useRoute";
import { uniqWith } from "lodash";

const isConditionalPage = (page: EcoaPage | EcoaPageVisibilitySpec): page is EcoaPageVisibilitySpec => {
  return "page" in page;
};

const isConditionalPages = (pages: Array<EcoaPage> | Array<EcoaPageVisibilitySpec>): pages is Array<EcoaPageVisibilitySpec> => {
  return "page" in pages[pages.length - 1];
};

interface PreScreeningQuestionnairesProps {
  questionnaires: Array<EcoaPage> | Array<EcoaPageVisibilitySpec>;
  studyId: bigint;
  siteId?: bigint;
  screeningCaptureData: Array<{
    itemId: string | null;
    dbVal: string | number | null;
    sectionId: string;
  }>;
  getPreScreeningParams: () => {
    encryptedPayload: string;
    captures: Array<StudyCaptureData>;
  };
  onSaveCaptureData: (data: Array<PreScreeningCaptureItemSpec>) => void;
}

export function PreScreeningQuestionnaires({
  studyId,
  siteId,
  questionnaires,
  screeningCaptureData,
  getPreScreeningParams,
  onSaveCaptureData,
}: PreScreeningQuestionnairesProps) {
  const { pushPath } = useRoute();

  const [disabledFooter, setDisabledFooter] = useState<boolean>(false);
  const params = useParams<{ questionnaireSeq: string }>();
  const { questionnaireSeq: questionnaireSeqVal } = params;
  const questionnaireSeq = useIntParam(questionnaireSeqVal);

  const validEcoaPages: Array<EcoaPage> | Array<EcoaPageVisibilitySpec> = ((pages) => {
    const isAvailability = ({ item: { availability } }: any) => {
      // TBD

      if (!availability || !availability.length) return true;
      const { condition, operand, id: targetId } = availability[0]; // TODO 우선 한개만 구현

      const targetData = screeningCaptureData.find((scd) => scd.itemId === targetId.itemId);

      if (!targetData) return true;
      if (condition === "=") {
        return targetData.dbVal === operand;
      } else if (condition === "!=") {
        return targetData.dbVal !== operand;
      }

      return true;
    };

    if (!pages.length) {
      return pages;
    } else if (isConditionalPages(pages)) {
      return pages.reduce<Array<EcoaPageVisibilitySpec>>((result, { page, conditional }) => {
        if (Array.isArray(page)) {
          const availablePages = page.filter((p) => isAvailability(p));

          if (availablePages.length) {
            result.push({ page: availablePages, conditional });
          }
        } else {
          if (isAvailability(page)) {
            result.push({ page, conditional });
          }
        }

        return result;
      }, []);
    } else {
      return pages.reduce<Array<EcoaPage>>((result, page) => {
        if (Array.isArray(page)) {
          const availablePages = page.filter((p) => isAvailability(p));

          if (availablePages.length) {
            result.push(availablePages);
          }
        } else {
          if (isAvailability(page)) {
            result.push(page);
          }
        }

        return result;
      }, []);
    }
  })(questionnaires);

  const filteredQuestionnaires = useMemo(() => {
    const processedPages = validEcoaPages
      .map((questionnairePage) => {
        const page = "page" in questionnairePage ? questionnairePage.page : questionnairePage;

        if (Array.isArray(page)) {
          return uniqWith(page.filter(supportedItemFilter), questionnaireUniqComparator);
        }

        return supportedItemFilter(page) ? page : null;
      })
      .filter(isPresent);

    return uniqWith(processedPages, (a, b) => {
      if (!Array.isArray(a) && !Array.isArray(b)) {
        return questionnaireUniqComparator(a, b);
      }
      return false;
    });
  }, [validEcoaPages]);

  const questionnaireItem = filteredQuestionnaires[questionnaireSeq - 1];

  const questionnaire = (() => {
    if (questionnaireItem && isConditionalPage(questionnaireItem)) {
      return questionnaireItem.page;
    }

    return questionnaireItem;
  })();

  const isLastQuestionnaire = filteredQuestionnaires.length === questionnaireSeq;

  const handleClickNext = () => {
    pushPath(`/${studyId}/pre/pre-screening/${questionnaireSeq + 1}`);
    window?.scrollTo(0, 0);
  };

  const handleDisabledFooterChange = (val: boolean) => {
    setDisabledFooter(val);
  };

  if (
    questionnaireItem &&
    isConditionalPage(questionnaireItem) &&
    questionnaireItem.conditional &&
    !isConditionalMatched(questionnaireItem.conditional, getPreScreeningParams().captures)
  ) {
    pushPath(`/${studyId}/pre/pre-screening/failed`);
  }
  if (!questionnaire) return <Redirect to={`/${studyId}/pre/pre-screening`} />;

  const chooseAnswersAllInStep = ((data, questionnaire): boolean => {
    if (!data.length) return false;

    if (Array.isArray(questionnaire)) {
      return !questionnaire.some((q) => {
        if (q.item.original.type === "CHECK") {
          return q.item.original.itemCode.codes.every((code) => {
            const dbVal = data.find((item) => item.itemId === code.id)?.dbVal;
            return !dbVal && typeof dbVal !== "number";
          });
        } else {
          const dbVal = data.find((item) => item.itemId === q.item.id)?.dbVal;
          return !dbVal && typeof dbVal !== "number";
        }
      });
    } else {
      if (questionnaire.item.original.type === "CHECK") {
        return questionnaire.item.original.itemCode.codes.some((code) => {
          const dbVal = data.find((item) => item.itemId === code.id)?.dbVal;
          return !!dbVal || typeof dbVal === "number";
        });
      } else {
        const dbVal = data.find((item) => item.itemId === questionnaire.item.id)?.dbVal;
        return !!dbVal || typeof dbVal === "number";
      }
    }
  })(screeningCaptureData, questionnaire);

  return Array.isArray(questionnaireItem) ? (
    <PreScreeningMultipleQuestionnaire
      key={`PreScreeningMultipleQuestionnaire-${questionnaireSeq}`}
      screeningCaptureData={screeningCaptureData}
      questionnaires={questionnaireItem}
      onChangeAnswers={onSaveCaptureData}
      onChangeDisabledFooter={handleDisabledFooterChange}
    >
      <PreScreeningQuestionnaireFooter
        getPreScreeningParams={getPreScreeningParams}
        disabled={disabledFooter || !chooseAnswersAllInStep}
        studyId={studyId}
        siteId={siteId}
        isLastQuestionnaire={isLastQuestionnaire}
        onClickNext={handleClickNext}
      />
    </PreScreeningMultipleQuestionnaire>
  ) : (
    <PreScreeningSingleQuestionnaire
      key={`PreScreeningSingleQuestionnaire-${questionnaireSeq}`}
      screeningCaptureData={screeningCaptureData}
      questionnaire={questionnaireItem}
      onChangeDisabledFooter={handleDisabledFooterChange}
      onChangeAnswers={onSaveCaptureData}
    >
      <PreScreeningQuestionnaireFooter
        getPreScreeningParams={getPreScreeningParams}
        disabled={disabledFooter || !chooseAnswersAllInStep}
        studyId={studyId}
        siteId={siteId}
        isLastQuestionnaire={isLastQuestionnaire}
        onClickNext={handleClickNext}
      />
    </PreScreeningSingleQuestionnaire>
  );
}

function questionnaireUniqComparator(a: EcoaQuestionnaire, b: EcoaQuestionnaire): boolean {
  if (a.item.type === "DESCRIPTION" && b.item.type === "DESCRIPTION") {
    return false;
  }
  return a.item.groupUuid === b.item.groupUuid;
}

// NOTE: 지원되지 않는 Item이거나 hideOnEcoa인 경우 Rendering 되지 않도록 제외.
function supportedItemFilter(q: EcoaQuestionnaire): boolean {
  const SUPPORTED_ITEM_TYPES = [
    "CHECK_GROUP",
    "SINGLE_SELECT",
    "TEXT",
    "CHECK",
    "DATE",
    "TIME",
    "CONSTANT",
    "AUTO_TEXT",
    "SLIDER",
    "DESCRIPTION",
  ];

  return SUPPORTED_ITEM_TYPES.includes(q?.item?.type) && !q.item.original.ecoa?.hideOnEcoa;
}

function isPresent(value: EcoaQuestionnaire | Array<EcoaQuestionnaire> | null): value is EcoaQuestionnaire | Array<EcoaQuestionnaire> {
  return Array.isArray(value) ? value.length > 0 : !!value;
}
